import React, { useEffect, useState } from "react";
import { AlertTriangle, Download, FileText, Info, Loader2, ShieldCheck, X } from "lucide-react";
import { masterSupabase } from "@/lib/supabase-master";
import { Button } from "@/components/ui/button";

const STATUS = {
  OCR_VERIFIED: {
    label: "OCR-verifiziert",
    description: "Der sichtbare Evidenzausschnitt stammt aus dem maschinell lesbaren Original. Inhaltliche Aussagen dürfen darauf gestützt werden.",
    className: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800",
    icon: ShieldCheck,
  },
  METADATA_ONLY: {
    label: "Metadaten",
    description: "Das Dokument ist objektbezogen vorhanden. Daraus dürfen nur Existenz, Zuordnung und Metadaten abgeleitet werden.",
    className: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
    icon: Info,
  },
  // Compatibility for previously emitted evidence payloads.
  METADATA: {
    label: "Metadaten",
    description: "Das Dokument ist objektbezogen vorhanden. Daraus dürfen nur Existenz, Zuordnung und Metadaten abgeleitet werden.",
    className: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
    icon: Info,
  },
  INSUFFICIENT: {
    label: "Unzureichende Evidenz",
    description: "Für dieses Artefakt steht derzeit kein abrufbarer Originalbeleg mit belastbarer Evidenz zur Verfügung. Clara darf daraus keine fachliche Aussage ableiten.",
    className: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800",
    icon: AlertTriangle,
  },
};

export default function EvidenceViewerModal({ artifactReference, isOpen, onClose }) {
  const [artifact, setArtifact] = useState(null);
  const [originalUrl, setOriginalUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    let blobUrl = null;

    const load = async () => {
      if (!isOpen || !artifactReference) return;
      setArtifact(null);
      setOriginalUrl(null);
      setError(null);
      setLoading(true);
      try {
        const { data } = await masterSupabase.auth.getSession();
        const token = data?.session?.access_token;
        if (!token) throw new Error("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
        const headers = { Authorization: `Bearer ${token}` };
        const metadataResponse = await fetch(`/api/evidence/artifacts/${encodeURIComponent(artifactReference)}`, { headers });
        if (!metadataResponse.ok) {
          if (metadataResponse.status === 404) throw new Error("Dieses Artefakt wurde nicht gefunden oder gehört nicht zu Ihrer Organisation.");
          throw new Error("Evidenz konnte derzeit nicht geladen werden.");
        }
        const payload = await metadataResponse.json();
        if (!active) return;
        setArtifact(payload.artifact);

        if (payload.artifact?.original_available) {
          setLoadingOriginal(true);
          const originalResponse = await fetch(`/api/evidence/artifacts/${encodeURIComponent(artifactReference)}/original`, { headers });
          if (originalResponse.ok) {
            const blob = await originalResponse.blob();
            blobUrl = URL.createObjectURL(blob);
            if (active) setOriginalUrl(blobUrl);
          }
          if (active && !originalResponse.ok) {
            setError("Die Evidenzdaten sind vorhanden, der Originalbeleg ist momentan jedoch nicht abrufbar.");
          }
          if (active) setLoadingOriginal(false);
        }
      } catch (cause) {
        if (active) setError(cause?.message || "Evidenz konnte nicht geladen werden.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [artifactReference, isOpen]);

  if (!isOpen) return null;
  const status = STATUS[artifact?.evidence_status] || STATUS.INSUFFICIENT;
  const StatusIcon = status.icon;
  const isPdf = String(artifact?.mime_type || "").toLowerCase().includes("pdf");
  const canDownload = Boolean(originalUrl);

  const close = () => {
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    onClose();
  };

  const download = () => {
    if (!originalUrl || !artifact?.filename) return;
    const link = document.createElement("a");
    link.href = originalUrl;
    link.download = artifact.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label="Evidenzbeleg">
      <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <FileText className="h-4 w-4" /> Evidenzbeleg
            </div>
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-white">{artifact?.filename || "Artefakt wird geladen …"}</h2>
            {artifact?.property_name && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Objekt: {artifact.property_name}</p>}
          </div>
          <div className="flex items-center gap-2">
            {canDownload && <Button onClick={download} variant="outline" size="sm" className="gap-2"><Download className="h-4 w-4" />Herunterladen</Button>}
            <Button onClick={close} variant="ghost" size="icon" aria-label="Viewer schließen"><X className="h-5 w-5" /></Button>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section className="min-h-0 bg-slate-100 p-3 dark:bg-slate-950">
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-600 dark:text-slate-300"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Evidenz wird geprüft …</div>
            ) : error && !artifact ? (
              <div className="flex h-full items-center justify-center"><div className="max-w-md rounded-xl border border-rose-200 bg-rose-50 p-5 text-center text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200"><AlertTriangle className="mx-auto mb-3 h-7 w-7" />{error}</div></div>
            ) : loadingOriginal ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-600 dark:text-slate-300"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Originalbeleg wird sicher geladen …</div>
            ) : originalUrl && isPdf ? (
              <iframe title={artifact?.filename || "Originalbeleg"} src={originalUrl} className="h-full min-h-[420px] w-full rounded-lg border border-slate-200 bg-white dark:border-slate-700" />
            ) : originalUrl ? (
              <div className="flex h-full items-center justify-center"><img src={originalUrl} alt={artifact?.filename || "Originalbeleg"} className="max-h-full max-w-full rounded-lg object-contain shadow" /></div>
            ) : (
              <div className="flex h-full items-center justify-center"><div className="max-w-md rounded-xl border border-amber-200 bg-amber-50 p-5 text-center text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"><FileText className="mx-auto mb-3 h-8 w-8" />Für dieses Artefakt ist derzeit kein Originalbeleg im Evidenzarchiv abrufbar.</div></div>
            )}
          </section>

          <aside className="overflow-y-auto border-t border-slate-200 p-5 dark:border-slate-700 lg:border-l lg:border-t-0">
            {artifact && <>
              <div className={`mb-5 rounded-xl border p-3 ${status.className}`}>
                <div className="flex items-center gap-2 font-semibold"><StatusIcon className="h-4 w-4" />{status.label}</div>
                <p className="mt-2 text-xs leading-5">{status.description}</p>
              </div>
              <dl className="space-y-3 text-sm">
                <div><dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Dokument</dt><dd className="mt-1 break-words text-slate-900 dark:text-white">{artifact.filename}</dd></div>
                <div><dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Objekt</dt><dd className="mt-1 text-slate-900 dark:text-white">{artifact.property_name || "Nicht objektbezogen"}</dd></div>
                {artifact.document_type && <div><dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Typ</dt><dd className="mt-1 text-slate-900 dark:text-white">{artifact.document_type}</dd></div>}
              </dl>
              {artifact.evidence_status === "OCR_VERIFIED" && artifact.evidence_excerpt && <div className="mt-6 border-t border-slate-200 pt-5 dark:border-slate-700"><p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">OCR-Evidenzausschnitt</p><blockquote className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm leading-6 text-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-100">{artifact.evidence_excerpt}</blockquote></div>}
              {error && <p className="mt-5 rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">{error}</p>}
            </>}
          </aside>
        </div>
      </div>
    </div>
  );
}
